import { MinimizedArticleStyled } from "@components/Article/MiniArticle";
import ArticleContextWrapper from "@core-ui/ScopedContextWrapper/ArticleContextWrapper";
import { DiffLinePopoverHeader, DiscardButton } from "@ext/git/core/Diff/components/DiffLine/DiffLinePopover";
import attributeRows from "@ext/git/core/Diff/logic/attributeRows";
import { resolveCompareOptions } from "@ext/git/core/Diff/logic/canonicalChanges";
import charDiff from "@ext/git/core/Diff/logic/charDiff";
import type { DiffLineType } from "@ext/git/core/Diff/logic/model/DiffLine";
import type { TreeReadScope } from "@ext/git/core/GitCommands/model/GitCommandsModel";
import t from "@ext/localization/locale/translate";
import getExtensions from "@ext/markdown/core/edit/logic/getExtensions";
import ElementGroups from "@ext/markdown/core/element/ElementGroups";
import CommentEditorProvider from "@ext/markdown/elements/comment/edit/logic/CommentEditorProvider";
import Comment from "@ext/markdown/elements/comment/edit/model/comment";
import { DiffExtension } from "@gaurussel/tiptap-diff-utility";
import Document from "@tiptap/extension-document";
import { EditorContent, type JSONContent, useEditor } from "@tiptap/react";
import { useMemo } from "react";

interface ProsemirrorDiffLineContentProps {
	oldContent: JSONContent;
	newContent: JSONContent;
	oldScope: TreeReadScope;
	type: Exclude<DiffLineType, "added" | "comment">;
	articlePath: string;
	onDiscard?: () => void;
}

const REMOVED_CLASS = "rounded-sm bg-[var(--color-removed-bg)] text-[var(--color-removed-text)] no-underline";
const ADDED_CLASS = "rounded-sm bg-[var(--color-added-bg)] text-[var(--color-added-text)] no-underline";

const CharDiffText = ({ from, to }: { from: string; to: string }) => (
	<span className="break-all">
		{charDiff(from, to).map((part) => {
			if (part.kind === "removed")
				return (
					<del className={REMOVED_CLASS} key={part.key}>
						{part.text}
					</del>
				);
			if (part.kind === "added")
				return (
					<ins className={ADDED_CLASS} key={part.key}>
						{part.text}
					</ins>
				);
			return <span key={part.key}>{part.text}</span>;
		})}
	</span>
);

const ProsemirrorDiffLineContent = (props: ProsemirrorDiffLineContentProps) => {
	const { oldContent, newContent, onDiscard, oldScope, type, articlePath } = props;

	// Resource nodes must be listed explicitly, or the popover editor throws
	// `Unknown node type: image`.
	const extensions = useMemo(
		() => [...getExtensions({ includeResources: true }), Comment.configure({ appendCommentToBody: true })],
		[],
	);

	const editor = useEditor(
		{
			extensions: [
				...extensions,
				DiffExtension.configure({
					...resolveCompareOptions(),
					baseline: newContent,
					mode: type === "breadcrumb" ? "unified" : "deletions",
				}),
				Document.configure({ content: `paragraph ${ElementGroups.block}+` }),
			],
			content: oldContent,
			editable: false,
		},
		[oldContent],
	);

	const attrs = useMemo(
		() => (editor && type === "modified" ? attributeRows(oldContent, newContent, editor.schema) : null),
		[editor, oldContent, newContent, type],
	);
	const hasAttrs = !!attrs && attrs.rows.length + attrs.removedLinks.length + attrs.addedLinks.length > 0;

	const body = (
		<div className="article bg-[var(--color-article-bg)]" data-qa="diff-line-popover">
			<DiffLinePopoverHeader
				action={
					type === "modified" && onDiscard && <DiscardButton onClick={onDiscard} qa="diff-line-discard" />
				}
				title={type === "modified" ? t("diff.previous-version") : t(`diff.type.${type}`)}
			/>
			<div className="max-h-[50vh] overflow-auto px-3 pb-3">
				<MinimizedArticleStyled>
					<div className="article-body popup-article diff-previous-version [&_:is(p,ul,ol,li):last-child]:!mb-0">
						<CommentEditorProvider editor={editor}>
							<EditorContent data-iseditable={false} editor={editor} />
						</CommentEditorProvider>
					</div>
				</MinimizedArticleStyled>
				{hasAttrs && attrs && (
					<section
						aria-label={t("diff.attrs.title")}
						className="mt-2 border-t border-[var(--color-line)] pt-2 text-xs"
					>
						<div className="mb-1 font-medium text-muted">{t("diff.attrs.title")}</div>
						{attrs.rows.map((row) => (
							<div key={`${row.label}-${row.from}-${row.to}`}>
								{row.label}: <CharDiffText from={row.from} to={row.to} />
							</div>
						))}
						{attrs.removedLinks.length > 0 && (
							<section aria-label={t("diff.attrs.links-removed")}>
								<div className="text-muted">{t("diff.attrs.links-removed")}</div>
								{attrs.removedLinks.map((link) => (
									<div key={`${link.text}-${link.href}`}>
										«{link.text}» — {link.href}
									</div>
								))}
							</section>
						)}
						{attrs.addedLinks.length > 0 && (
							<section aria-label={t("diff.attrs.links-added")}>
								<div className="text-muted">{t("diff.attrs.links-added")}</div>
								{attrs.addedLinks.map((link) => (
									<div key={`${link.text}-${link.href}`}>
										«{link.text}» — {link.href}
									</div>
								))}
							</section>
						)}
					</section>
				)}
			</div>
		</div>
	);

	// Without a revision there is nothing to scope: an empty scope fails an assertion
	// and the line renders as an empty box.
	if (!oldScope) return body;

	return (
		<ArticleContextWrapper articlePath={articlePath} scope={oldScope}>
			{body}
		</ArticleContextWrapper>
	);
};

export default ProsemirrorDiffLineContent;
